// File: src/components/general/Typography/Typography.tsx
import React, { JSX } from "react";
import styled, { css, keyframes } from "styled-components";
import { DefaultTheme } from "styled-components"; // ✅ Import Theme Type

export type TypographyProps = {
  children: React.ReactNode;
  className?: string;
  as?: keyof JSX.IntrinsicElements; // Allows h1, h2, span, etc.
  variant?: keyof DefaultTheme["typography"]; // ✅ Fix: Use Theme Type
  color?: string;
  colorScheme?: keyof DefaultTheme["colors"]; // ✅ Fix: Use Theme Type
  weight?: "light" | "regular" | "medium" | "bold" | "extraBold";
  align?: "left" | "center" | "right";
  textTransform?: "uppercase" | "lowercase" | "capitalize" | "none";
  ellipsis?: boolean; // Single-line ellipsis
  multiLineEllipsis?: number; // Multi-line ellipsis
  hoverEffect?: boolean; // Hover transition effect
  animated?: boolean; // Simple fade-in animation
  style?: React.CSSProperties; // ✅ Support inline styles

};

/** 🔄 Fade-in animation */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StyledTypography = styled.p<TypographyProps>`
  font-family: ${({ theme }) => theme.fonts.fontFamily};
  text-align: ${({ align }) => align || "left"};
  display: ${({ as }) => (as === "span" || as === "button" ? "inline-flex" : "block")};
  align-items: center;

  ${({ variant = "default", theme }) => css`
    font-size: ${theme.typography[variant]?.fontSize || "19px"};
    line-height: ${theme.typography[variant]?.lineHeight|| "20px"};
    letter-spacing: ${theme.typography[variant]?.letterSpacing || "normal"};

   @media (max-width: ${theme.breakpoints.mobile}) {
        font-size: ${theme.typography[variant]?.mobileSize || theme.typography[variant]?.fontSize || "14px"};
        line-height: ${theme.typography[variant]?.mobileLineHeight || theme.typography[variant]?.lineHeight || "20px"};
      }
  `}

  font-weight: ${({ weight = "regular", theme }) => {
    const weights = {
      light: theme.fonts.fontWeightLight,
      medium: theme.fonts.fontWeightMedium,
      bold: theme.fonts.fontWeightBold,
      extraBold: theme.fonts.fontWeightExtraBold,
      regular: theme.fonts.fontWeightRegular,
    };
    return weights[weight];
  }};

  text-transform: ${({ textTransform }) => textTransform || "none"};
  font-style: ${({ variant }) => (variant === "quote" ? "italic" : "normal")};

  color: ${({ color, colorScheme, theme }) => color || (colorScheme ? theme.colors[colorScheme] : theme.colors.textPrimary)};

  ${({ ellipsis }) =>
    ellipsis &&
    css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `}

  ${({ multiLineEllipsis }) =>
    multiLineEllipsis &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: ${multiLineEllipsis};
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}

  ${({ hoverEffect, theme }) =>
    hoverEffect &&
    css`
      transition: color 0.2s ease-in-out;
      &:hover {
        color: ${theme.colors.primaryHover};
      }
    `}

  ${({ animated }) =>
    animated &&
    css`
      animation: ${fadeIn} 0.3s ease-in-out;
    `}
`;

export const Typography: React.FC<TypographyProps> = ({ children, as = "p", className, style, ...props }) => {
  return <StyledTypography className={className} as={as} style={style}  {...props}>{children}</StyledTypography>;
};